import type { ProtectedRouteOptions } from "../../routing-services";
import { ExtendedUserInfo, useAuthentication } from "../authentication";
import { configureRouter } from "@/.shared/services/routing-services";
import NotAuthorizedView from "@views/NotAuthorizedView.vue";
import { useIntl } from "../../i18n";
import type { RouteLocationNormalized } from "vue-router";


export interface AuthorizationService {
    getUserPermissions(): Iterable<string>
    hasPermission(permission: string): boolean;
}

export interface AuthorizationServiceConfiguration {
    commonRoutePermissionChecker?: (userInfo: ExtendedUserInfo, route: RouteLocationNormalized) => boolean;
}

export class AuthorizationServiceImpl implements AuthorizationService {
    private _auth: ReturnType<typeof useAuthentication>;

    constructor(cfg: AuthorizationServiceConfiguration) {
        this._auth = useAuthentication();
    }

    public *getUserPermissions(): Iterable<string> {
        for (const permission of this.userInfo.permissions ?? []) {
            yield permission;
        }
    }

    public hasPermission(permission: string) {
        return this.userInfo.permissions?.has(permission) ?? false;
    }

    get userInfo() : ExtendedUserInfo {
        return this._auth.userInfo as ExtendedUserInfo;
    }
}

let instance: AuthorizationServiceImpl;



export function configureAuthorization(cfg: AuthorizationServiceConfiguration) {
    instance = new AuthorizationServiceImpl(cfg);
    configureRouter(router => {

        router.addRoute("not-authorized", b => b.withPath("not-authorized").withComponent(NotAuthorizedView).allowAnonymous());

        router.beforeEach(async (to, from, next) => {
            const prot = to.meta.protection as ProtectedRouteOptions;
            let destination = to.fullPath
            const isRedirecting = to.query.__firstRedirect === "true";

            const authentication = useAuthentication();
            const check = (resolve: (...args: any[]) => void) => {
                if (authentication.isInititalized) {
                    resolve();
                }
                else {
                    setTimeout(() => check(resolve), 50);
                }
            }
           
            await new Promise(resolve => check(resolve));
            
            if (isRedirecting) {
                const url = new window.URL("https://dummy" + to.fullPath);
                url.searchParams.delete("__firstRedirect");
                destination = `${url.pathname}${url.search}${url.hash}`;
            }

            if (prot) {
           
                //Verificamos si el usuario está logueado y gestionamos redirecciones
                let canHaveAccess = authentication.isLoggedIn();
                let isInitialLoad = from.matched.length == 0;

                if (!canHaveAccess) {
                    if (isInitialLoad && !isRedirecting) {
                        const url = new window.URL("https://dummy" + to.fullPath);
                        url.searchParams.append("__firstRedirect", "true");
                        authentication.login(`${url.pathname}${url.search}${url.hash}`);
                    }
                    else
                        return next(useIntl().i18Route({ name: "home", query: { redirectTo: destination } }));
                }

                //Aplicamos políticas comunes
                if (canHaveAccess && cfg.commonRoutePermissionChecker) {
                    canHaveAccess = cfg.commonRoutePermissionChecker(instance.userInfo, to);
                }

                //Aplicamos políticas específicas de la ruta
                if (canHaveAccess) {
                    if (typeof prot.authReqs === "function") {
                        canHaveAccess = prot.authReqs(instance.userInfo, to);
                    }
                    else if (Array.isArray(prot.authReqs)) {
                        for (const permission of prot.authReqs) {
                            if (!instance.hasPermission(permission)) {
                                canHaveAccess = false;
                                break;
                            }
                        }
                    }
                }

                if (!canHaveAccess) {
                    return next(useIntl().i18Route({ name: "not-authorized", query: { redirectTo: "/" } }));
                }
            }

            if (destination !== to.fullPath)
                next(destination);
            else
                next();
        });
    })

}

export function useAuthorization(): AuthorizationService {
    return instance;
}

export enum PermissionsApp {

    AdminAlarmero = 'ADMIN_ALARMERO',
    AdminQuely = "ADMIN_QUELY",
    AdminExcelPartners = "ADMIN_EXCEL_PARTNERS",

    //Revenue
    EditAllUsuariosRevenue = 'EDIT_ALL_USUARIOS_REVENUE',
    UserRssync = 'USER_RSSYNC',
    EditAllPartners = 'EDIT_ALL_PARTNERS',
    EditSelfPartners = 'EDIT_SELF_PARTNERS',
    EditAllActivosRevenue = 'EDIT_ALL_ACTIVOS_REVENUE',
    EditSelfActivosRevenue = 'EDIT_SELF_ACTIVOS_REVENUE',
    EditAllTransacciones = 'EDIT_ALL_TRANSACCIONES',
    EditSelfTransacciones = 'EDIT_SELF_TRANSACCIONES',
    ReadSelfTransacciones = 'READ_SELF_TRANSACCIONES',
    EditUsuariosPartners = 'EDIT_USUARIOS_PARTNERS',

    //GMAO - COMPLIANCE
    EditAllActivosGmao = 'EDIT_ALL_ACTIVOS_GMAO',
    EditSelfActivosGmao = 'EDIT_SELF_ACTIVOS_GMAO',
    GestionAvanzadaActuacionesAll = 'GESTION_AVANZADA_ACTUACIONES_ALL',
    GestionAvanzadaActuacionesSelf = 'GESTION_AVANZADA_ACTUACIONES_SELF',
    GestionBasicaActuacionesSelf = 'GESTION_BASICA_ACTUACIONES_SELF',
    UserLoveCharger = 'USER_LOVECHARGER',
    CreateActuaciones = 'CREATE_ACTUACIONES',
    CreateWorkOrderCaliente = 'CREATE_WORK_ORDER_CALIENTE',
    EditAllUsuariosGmao = 'EDIT_ALL_USUARIOS_GMAO',
    EditPrestadores = 'EDIT_PRESTADORES',
    EditSelfActuacionesVistaTecnico = 'EDIT_SELF_ACTUACIONES_VISTA_TECNICO',
    EditSelfUsuariosPrestadorGmao = 'EDIT_SELF_USUARIOS_PRESTADOR_GMAO',
    AsignarTecnicoFechaActuacion = 'ASIGNAR_TECNICO_FECHA_ACTUACION',
    EditarResponsableFechasActuacion = "EDITAR_RESPONSABLE_FECHAS_ACTUACION",
    EditarTecnicoActuacion = "EDITAR_TECNICO_ACTUACION",
    EditarDesplazamientoActuacion = "EDITAR_DESPLAZAMIENTO_ACTUACION",
    EditSelfActuaciones = "EDIT_SELF_ACTUACIONES",
    EditarSubestadoWo = "EDITAR_SUBESTADO_WO",
    EditarComentarioWo = "EDITAR_COMENTARIO_WO",
    EditarInformeWo = "EDITAR_INFORME_WO",
    ReadAllEquipoMedida = "READ_ALL_EQUIPO_MEDIDA", 
    EditSelfEquipoMedida = "EDIT_SELF_EQUIPO_MEDIDA", 
    ReadSelfEquipoMedida = "READ_SELF_EQUIPO_MEDIDA", 
    EditActuacionUrgenciaAdministrador = "EDIT_ACTUACION_URGENCIA_ADMINISTRADOR",
    EditAllWo = "EDIT_ALL_WO",
    EditSelfWo = "EDIT_SELF_WO",
    CreateActivos = "CREATE_ACTIVOS",
    //Compliance
    EditarComplianceWo = "EDITAR_COMPLIANCE_WO",
    EditarComplianceIv = "EDITAR_COMPLIANCE_IV",
    GestionSuperAvanzadaActuacionesAll = "GESTION_SUPER_AVANZADA_ACTUACIONES_ALL",
    DescargarFicherosWo = "DESCARGAR_FICHEROS_WO",
    //SAT
    GestionTicketingAll = "GESTION_TICKETING_ALL",
    CreateWoFromTicketAll = "CREATE_WO_FROM_TICKET_ALL",
    ReadAllActuaciones = "READ_ALL_ACTUACIONES",
    ReadAllWo = "READ_ALL_WO",
    ReadAllCampanas = "READ_ALL_CAMPANAS",
    ReadAllActivosGmao = "READ_ALL_ACTIVOS_GMAO",
    ReadAllUbicaciones = "READ_ALL_UBICACIONES",
    ReadAllCentrosTransformacion = "READ_ALL_CENTROS_TRANSFORMACION",
    ReadAllInspeccionesVisuales = "READ_ALL_INSPECCIONES_VISUALES",
    ReadAllOcas = "READ_ALL_OCAS",
    //OCA
    GestionAvanzadaOcasAll = "GESTION_AVANZADA_OCAS_ALL",
    GestionOcasSelf = "GESTION_OCAS_SELF",
    GestionAvanzadaOcasSelf = "GESTION_AVANZADA_OCAS_SELF",
    GestionBasicaOcasSelf = "GESTION_BASICA_OCAS_SELF",
    GestionOcasBtSelf = "GESTION_OCAS_BT_SELF",
    //INSPECCIONES VISUALES
    GestionAvanzadaInspeccionesVisualesAll = "GESTION_AVANZADA_INSPECCIONES_VISUALES_ALL",
    GestionAvanzadaInspeccionesVisualesSelf = "GESTION_AVANZADA_INSPECCIONES_VISUALES_SELF",
    GestionBasicaInspeccionesVisualesSelf = "GESTION_BASICA_INSPECCIONES_VISUALES_SELF",


    //Se van a borrar en la siguiente versión
    ResponsablePds= 'RESPONSABLE_PDS',
    TrabajadorEndesa = 'TRABAJADOR_ENDESA',
    TecnicoPds = 'TECNICO_PDS',
    Partner = "PARTNER",
    //Revenue

    //Campanas
    GestionCampanas = "GESTION_CAMPANAS",

    //Gestion de uso IA de usuarios
    GestionarIaUsuarios = "GESTIONAR_IA_USUARIOS",
}

export enum RolesApp{
    GestionGMAO = 'GESTION_GMAO',
    GestionCompliance = 'GESTION COMPLIANCE',
    ResponsablePrestador = 'RESPONSABLE_PRESTADOR',
    TecnicoPrestador = 'TECNICO_PRESTADOR',
    EchargerSat = 'ECHARGER_SAT',
    GestionRevenue = 'REVENUE',
    Partner = 'PARTNER',
    BD = 'BD',    
}